import { get } from 'lodash';
import moment from 'moment';
import * as yup from 'yup';
import {
  PAYMENT_METHOD_CATEGORY,
  VOXPAY_PSP_ID_PREFIX,
} from 'Components/widgets/payment-info/PaymentInfo.const';
import { t } from 'Src/i18n';
import { API_DATE_FORMAT } from 'Utils/date/apiDate';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { PAYMENT_METHOD_ID_DIRECT_DEBIT } from 'Utils/payment/constants';
export const getEnabledPaymentProviders = (
  paymentProviders: ReadonlyArray<PaymentProvider> | null | undefined,
  isMobile: boolean,
  appContext: AppContext,
): Array<PaymentProvider> => {
  return (paymentProviders || []).filter((provider) => {
    return (
      isFeatureFlipped(
        appContext,
        isMobile,
      )(`psp.${get(provider, 'selectorId', '').toLowerCase()}`) || provider.ignore
    );
  });
};
type args = {
  paymentProviders: ReadonlyArray<PaymentProvider>;
  arrivalDate: string;
  locale: string;
};
export const sanitizePaymentProviders = ({
  paymentProviders,
  arrivalDate,
  locale,
}: args): ReadonlyArray<PaymentProvider> => {
  const daysBeforeTravel = moment(arrivalDate, API_DATE_FORMAT).diff(moment(), 'days');
  const filteredPaymentProviders = paymentProviders.filter(
    ({ requiredDelayBeforeDeparture }) =>
      !requiredDelayBeforeDeparture ||
      requiredDelayBeforeDeparture === 0 ||
      requiredDelayBeforeDeparture < daysBeforeTravel,
  );

  if (locale === 'de-DE' && daysBeforeTravel <= 6) {
    return filteredPaymentProviders.map((psp) => ({
      ...psp,
      paymentMethods: psp.paymentMethods.filter(({ id }) => id !== PAYMENT_METHOD_ID_DIRECT_DEBIT),
    }));
  }

  return filteredPaymentProviders;
};
export const creditCardExpiryValidator = yup
  .mixed()
  .test('hasCreditCardExpired', 'Credit card has expired', (value) => {
    const expiryDate = moment(value);
    return expiryDate.isValid() && !expiryDate.isBefore(moment(), 'month');
  })
  .required();
export const creditCardCVCValidator = yup
  .string()
  .min(3)
  .max(4)
  .matches(/^[0-9]*$/)
  .required();
export const isTransfer = (paymentProvider: PaymentProvider) => {
  return paymentProvider.categoryPaymentMethod === PAYMENT_METHOD_CATEGORY.BANK_TRANSFER;
};
export const isCreditCard = (paymentProvider: PaymentProvider) =>
  paymentProvider.categoryPaymentMethod === PAYMENT_METHOD_CATEGORY.CREDIT_CARD;
export const isPayPal = (paymentProvider: PaymentProvider) =>
  paymentProvider.categoryPaymentMethod === PAYMENT_METHOD_CATEGORY.PAYPAL;
export const isBuyNowPayLater = (paymentProvider: PaymentProvider) =>
  paymentProvider.categoryPaymentMethod === PAYMENT_METHOD_CATEGORY.BUY_NOW_PAY_LATER;
export const isVoxpayProvider = (providerId?: string) =>
  providerId?.startsWith(VOXPAY_PSP_ID_PREFIX);
export const isCheque = (paymentProvider: PaymentProvider) =>
  paymentProvider.categoryPaymentMethod === PAYMENT_METHOD_CATEGORY.CHEQUE;
export const getOrderedPaymentProviders = (
  paymentProviders: ReadonlyArray<PaymentProvider>,
  displayOrder: string,
): ReadonlyArray<PaymentProvider> => {
  if (!displayOrder) {
    return paymentProviders;
  }

  const displayOrderSchema = displayOrder.split(',');
  return displayOrderSchema
    .map((id) => {
      return paymentProviders.filter(
        (paymentProvider) => paymentProvider.id === id.trim() || paymentProvider.ignore,
      );
    })
    .flat()
    .filter(Boolean);
};
export const getPaymentProviderLabel = ({
  paymentProvider,
  isTotalAmount,
}: {
  paymentProvider: PaymentProvider;
  isTotalAmount: boolean;
}) => {
  const amountLabelKey = isTotalAmount ? 'totalAmount' : 'depositAmount';
  const amountShortLabelKey = isTotalAmount ? 'totalAmount' : 'deposit';

  if (isTransfer(paymentProvider)) {
    return t(`booking.banking.paymentProvider.bankTransfer.${amountShortLabelKey}`);
  }

  if (isPayPal(paymentProvider)) {
    return t(`booking.banking.paymentProvider.paypal.${amountLabelKey}`);
  }

  if (isBuyNowPayLater(paymentProvider)) {
    return t('booking.banking.paymentProvider.creditCard.totalAmount');
  }

  return t(`booking.banking.paymentProvider.creditCard.${amountShortLabelKey}`);
};
export const handleClickPaymentProviderBlock = ({
  paymentProvider,
  formik,
  isDisabled,
  mustKeepPaymentMethod = false,
}: {
  paymentProvider: PaymentProvider;
  formik: any;
  isDisabled: boolean;
  mustKeepPaymentMethod?: boolean;
}) => {
  if (!isDisabled) {
    formik.setFieldValue('paymentProviderId', paymentProvider.id);
    formik.setFieldValue('selectorPaymentProviderId', paymentProvider.selectorId);
    !mustKeepPaymentMethod && formik.setFieldValue('paymentMethodId', '');
  }
};
